import { useCallback, useEffect, useState } from "react";
import "./manage-subscription.css";
import {
  AUTO,
  CHILD,
  CP,
  FOOD,
  MEDICINE,
  PRESS,
  PET,
  BASE_URL,
  CATEGORY_ID_MAP,
  CATEGORY_NAME_MAP,
  CATEGORY_COMPLETE_NAME_MAP
} from "../assets/constant";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { loggedIn } from "../redux/authSlice";
import { Alert } from "react-bootstrap";
import { Loader } from "../components/loader";
import { CustomModal } from "../components/custom-modal";
import { Categories } from "../components/manage-subscription/categories";
import { FilterModal } from "../components/manage-subscription/subscription-modal";

export const ManageSubscription = () => {
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [initialCategories, setInitialCategories] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [optAllStates, setOptAllStates] = useState({});
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showUnsubscribeModal, setShowUnsubscribeModal] = useState(false);
  const [unsubscribeCategoryId, setUnsubscribeCategoryId] = useState(null);
  const [isLoading, setIsLoading] = useState({
    gettingSubscriptions: false,
    autoMaker: false,
    autoModel: false
  });
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [selectedFilterCategory, setSelectedFilterCategory] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  const user = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  // Function to subscribe a category
  const subscribeCategory = async (id) => {
    try {
      setIsLoading(prev => ({ ...prev, gettingSubscriptions: true }));

      await axios.post(
        `${BASE_URL}/recall/v1/subscribe`,
        {
          user_id: user.user.id,
          category_id: id,
          opt_all: true,
          filters: []
        },
        {
          headers: { Authorization: `Bearer ${user.token}` }
        }
      );

      // Update local state
      setSelectedCategories(prev => [...prev, { category_id: id }]);
      setOptAllStates(prev => ({ ...prev, [id]: true }));
      setSelectedFilters(prev => ({ ...prev, [id]: [] }));

      // Refresh subscriptions
      fetchSubscriptions();

      // Show subscribe success message
      setSuccessMessage(`You have successfully subscribed to ${CATEGORY_COMPLETE_NAME_MAP[CATEGORY_NAME_MAP[id]] || "this category"}.`);
      setConfirmationModal(true);
    } catch (error) {
      console.error("Error subscribing to category:", error);
    } finally {
      setIsLoading(prev => ({ ...prev, gettingSubscriptions: false }));
    }
  };

  // Handle category card click
  const handleCategoryClick = (id) => {
    const isSelected = selectedCategories.some((cat) => cat.category_id === id);

    if (!isSelected) {
      // If not subscribed → Subscribe with opt_all: true
      subscribeCategory(id);
    } else {
      // If already subscribed
      setUnsubscribeCategoryId(id);
      setShowUnsubscribeModal(true);
    }
  };

  // Confirm unsubscribe
  const confirmUnsubscribe = async () => {
    try {
      setIsLoading(prev => ({ ...prev, gettingSubscriptions: true }));

      await axios.post(
        `${BASE_URL}/recall/v1/subscribe`,
        {
          user_id: user.user.id,
          category_id: unsubscribeCategoryId,
          opt_all: false,
          filters: []
        },
        {
          headers: { Authorization: `Bearer ${user.token}` }
        }
      );

      setSelectedCategories(prev =>
        prev.filter((cat) => cat.category_id !== unsubscribeCategoryId)
      );
      setOptAllStates(prev => {
        const newStates = { ...prev };
        delete newStates[unsubscribeCategoryId];
        return newStates;
      });
      setSelectedFilters(prev => {
        const newFilters = { ...prev };
        delete newFilters[unsubscribeCategoryId];
        return newFilters;
      });

      // Refresh subscriptions
      fetchSubscriptions();
 
      setShowUnsubscribeModal(false);
      setUnsubscribeCategoryId(null);
    } catch (error) {
      console.error("Error unsubscribing from category:", error);
    } finally {
      setIsLoading(prev => ({ ...prev, gettingSubscriptions: false }));
    }
  };

  // Handle saving filters from modal
  const handleSaveFilters = async (categoryId, filters) => {
    // Clean filters 
    const cleanedFilters = filters.map(filter => {
      const cleaned = {};
      Object.keys(filter).forEach(key => {
        if (filter[key] && filter[key].trim() !== '') {
          cleaned[key] = filter[key];
        }
      });
      return cleaned;
    }).filter(filter => Object.keys(filter).length > 0);

    const hasFilters = cleanedFilters.length > 0;
    const optAll = !hasFilters;

    try {
      setIsLoading(prev => ({ ...prev, gettingSubscriptions: true }));

      const requestData = {
        user_id: user.user.id,
        category_id: categoryId,
        opt_all: optAll,
        filters: hasFilters ? cleanedFilters : []
      };

      await axios.post(
        `${BASE_URL}/recall/v1/subscribe`,
        requestData,
        {
          headers: { Authorization: `Bearer ${user.token}` }
        }
      );

      if (hasFilters) {
        setSelectedFilters(prev => ({
          ...prev,
          [categoryId]: cleanedFilters
        }));
        setOptAllStates(prev => ({ ...prev, [categoryId]: false }));
      } else {
        setSelectedFilters(prev => {
          const newFilters = { ...prev };
          delete newFilters[categoryId];
          return newFilters;
        });
        setOptAllStates(prev => ({ ...prev, [categoryId]: true }));
      }

      if (!selectedCategories.some((cat) => cat.category_id === categoryId)) {
        setSelectedCategories(prev => [...prev, { category_id: categoryId }]);
      }

      // Refresh subscriptions
      fetchSubscriptions();

      // Reset to default message for filter updates
      setSuccessMessage(""); 
      setConfirmationModal(true);
      setShowFilterModal(false);
    } catch (error) {
      console.error("Error saving filters:", error);
    } finally {
      setIsLoading(prev => ({ ...prev, gettingSubscriptions: false }));
    }
  };

  // Handle delete subscription from modal
  const handleDeleteSubscription = async (categoryId) => {
    try {
      setIsLoading(prev => ({ ...prev, gettingSubscriptions: true }));

      await axios.post(
        `${BASE_URL}/recall/v1/subscribe`,
        {
          user_id: user.user.id,
          category_id: categoryId,
          opt_all: false,  
          filters: []     
        },
        {
          headers: { Authorization: `Bearer ${user.token}` }
        }
      );

      setSelectedCategories(prev => prev.filter((cat) => cat.category_id !== categoryId));
      setOptAllStates(prev => {
        const newStates = { ...prev };
        delete newStates[categoryId];
        return newStates;
      });
      setSelectedFilters(prev => {
        const newFilters = { ...prev };
        delete newFilters[categoryId];
        return newFilters;
      });

      // Refresh subscriptions
      fetchSubscriptions();
      
      // close filter modal
      setShowFilterModal(false);
    } catch (error) {
      console.error("Error unsubscribing from category:", error);
    } finally {
      setIsLoading(prev => ({ ...prev, gettingSubscriptions: false }));
    }
  };

  // Fetch subscriptions from API
  const fetchSubscriptions = useCallback(async () => {
    try {
      setIsLoading((prev) => ({ ...prev, gettingSubscriptions: true }));

      const response = await axios.post(
        `${BASE_URL}/recall/v1/get-subscriptions`,
        { user_id: user.user.id },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      const subscriptions = response.data.subscriptions || [];

      setSelectedCategories(subscriptions);
      setInitialCategories(subscriptions);

      const filterMap = {};
      const optAllMap = {};

      subscriptions.forEach((cat) => {
        // Check if opt_all is true (1)
        const isOptAll = cat.opt_all === 1;

        if (isOptAll) {
          // If opt_all is true, don't store any filters
          optAllMap[cat.category_id] = true;
          filterMap[cat.category_id] = [];
        } else if (cat.filters?.length > 0) {
          // Filter out null/empty filter objects
          const validFilters = cat.filters.filter(filter => {
            // For auto category
            if (cat.category_id === CATEGORY_ID_MAP[AUTO]) {
              return (filter.make && filter.make !== null) ||
                (filter.model && filter.model !== null) ||
                (filter.vin && filter.vin !== null);
            }
            // For other categories
            return (filter.brand && filter.brand !== null) ||
              (filter.product_name && filter.product_name !== null) ||
              (filter.upc_code && filter.upc_code !== null);
          });

          if (validFilters.length > 0) {
            filterMap[cat.category_id] = validFilters;
            optAllMap[cat.category_id] = false;
          } else {
            filterMap[cat.category_id] = [];
            optAllMap[cat.category_id] = true;
          }
        } else {
          // No filters or empty array
          filterMap[cat.category_id] = [];
          optAllMap[cat.category_id] = false;
        }
      });

      setSelectedFilters(filterMap);
      setOptAllStates(optAllMap);

      dispatch(loggedIn({ subscription: subscriptions }));
    } catch (error) {
      console.error("Error fetching subscriptions:", error);
    } finally {
      setIsLoading(prev => ({ ...prev, gettingSubscriptions: false }));
    }
  }, [dispatch, user.token, user.user.id]);

  // Open filter modal
  const handleFilterSelect = (categoryId) => {
    setSelectedFilterCategory(categoryId);
    setShowFilterModal(true);
  };

  // Fetch subscriptions on component mount
  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  const handleRedirect = () => {
    window.history.back();
  };

  return (
    <div
      className="subscriber-container"
      style={{ display: "flex", justifyContent: "center" }}>
      <div className="manage-subscription-container">
        <header>
          <div style={{ display: "flex", fontWeight: "600" }}>
            <button
              className="fa-solid fa-arrow-left"
              style={{
                fontSize: "16px",
                cursor: "pointer",
                border: "none",
                background: "none"
              }}
              onClick={handleRedirect}></button>
            <h4>Select Industry</h4>
          </div>
          <p style={{ fontSize: "14px" }}>
            Select categories for recall subscription
          </p>
        </header>

        {initialCategories.length === 0 && !isLoading.gettingSubscriptions && (
          <Alert
            variant="warning"
            style={{ margin: "10px", padding: "12px", height: "50px" }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <p>Please select at least one category to proceed</p>
            </div>
          </Alert>
        )}

        {confirmationModal && (
          <CustomModal
            isOpen={confirmationModal}
            heading="Manage Subscription"
            text={successMessage || "Subscription has been updated successfully."}
            closeModal={() => {
              setConfirmationModal(false);
              setSuccessMessage(""); // Reset message
            }}
            btnName="OK"
            onClick={() => {
              setConfirmationModal(false);
              setSuccessMessage(""); 
            }}
          />
        )}

        {isLoading.gettingSubscriptions && <Loader />}

        <Categories
          isLoading={isLoading}
          categories={categories}
          selectedCategories={selectedCategories}
          selectedFilters={selectedFilters}
          optAllStates={optAllStates}
          handleCategoryClick={handleCategoryClick}
          handleFilterSelect={handleFilterSelect}
        />

        {/* Filter Modal */}
        <FilterModal
          show={showFilterModal}
          categoryId={selectedFilterCategory}
          categoryName={
            CATEGORY_COMPLETE_NAME_MAP[
            CATEGORY_NAME_MAP[selectedFilterCategory]
            ]
          }
          filters={selectedFilters[selectedFilterCategory] || []}
          onClose={() => setShowFilterModal(false)}
          onSave={(filters) => {
            handleSaveFilters(selectedFilterCategory, filters);
          }}
          handleDeleteSubscription={handleDeleteSubscription}
          onSuccess={() => setConfirmationModal(true)}
        />

        {/* Unsubscribe Confirmation Modal */}
        <CustomModal
          isOpen={showUnsubscribeModal}
          heading="Unsubscribe Confirmation"
          text={`Are you sure you want to unsubscribe from ${CATEGORY_COMPLETE_NAME_MAP[
            CATEGORY_NAME_MAP[unsubscribeCategoryId]
            ] || "this category"
            }?`}
          closeModal={() => setShowUnsubscribeModal(false)}
          btnName="Yes, Unsubscribe"
          onClick={confirmUnsubscribe}
          secondaryBtn={true}
          secondaryBtnName="No, Keep"
          secondaryOnClick={() => setShowUnsubscribeModal(false)}
        />
      </div>
    </div>
  );
};

const categories = [
  { id: 1, icon: "fa-car", text: "Automobiles", value: AUTO },
  { id: 2, icon: "fa-utensils", text: "Food", value: FOOD },
  { id: 3, icon: "fa-medkit", text: "Medicine", value: MEDICINE },
  { id: 4, icon: "fa-child", text: "Children", value: CHILD },
  { id: 5, icon: "fa-users", text: "Consumer Products", value: CP },
  { id: 6, icon: "fa-bullhorn", text: "Press Release", value: PRESS },
  { id: 7, icon: "fa-bullhorn", text: "Pet", value: PET },
];